import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { useTheme } from "@mui/styles";
import { RTFOStepdownChart } from "./RTFOStepdownChart";
import { Edit, Refresh, Restore } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { RTFOFlushStepdownChart } from "./RTFOFlushStepdownChart";
import { RTFOStepdownSummaryTable } from "./RTFOStepdownSummaryTable";
import { useSocket } from "../../context/useSocket";
import { setIsLoadingData } from "../../reducers/rtfoSlice";
import { useDispatch } from "react-redux";
import { RTFOStepdownParams } from "./RTFOStepdownParams";
import { RTFOND2StepdownChartMemo } from "./RTFOND2StepdownChart";
import { RTFOND2StepdownDataTable } from "./RTFOND2StepdownDataTable";

export const RTFOEditStepdown = ({ currentRTFO, setEditStepdownVisible, darkMode, sdtType, preSandISIP }) => {
  
  const theme = useTheme();
  const { socket } = useSocket();
  const dispatch = useDispatch();

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const [editMode, setEditMode] = useState(true);
  const [stepdownData, setStepdownData] = useState(currentStage?.stepdownData);
  const [preSandStepdownData, setPreSandStepdownData] = useState(currentStage?.preSandStepdownData?.length > 0 ? currentStage?.preSandStepdownData : []);
  const [stepdownParams, setStepdownParams] = useState(() => {
    try {
      const wellName = currentStage?.wellName?.toLowerCase();
      const currentStageStepdownParams = {...currentStage?.stepdownParams} || {};
      if (!currentStageStepdownParams?.fcCasing1) currentStageStepdownParams.fcCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.fc;
      if (!currentStageStepdownParams?.plExpCasing1) currentStageStepdownParams.plExpCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.plExp;
      if (!currentStageStepdownParams?.fcCasing2) currentStageStepdownParams.fcCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.fc;
      if (!currentStageStepdownParams?.plExpCasing2) currentStageStepdownParams.plExpCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.plExp;
      if (!currentStageStepdownParams?.preSandFCCasing1) currentStageStepdownParams.preSandFCCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.fc;
      if (!currentStageStepdownParams?.preSandPLExpCasing1) currentStageStepdownParams.preSandPLExpCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.plExp;
      if (!currentStageStepdownParams?.preSandFCCasing2) currentStageStepdownParams.preSandFCCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.fc;
      if (!currentStageStepdownParams?.preSandPLExpCasing2) currentStageStepdownParams.preSandPLExpCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.plExp;
      if (!currentStageStepdownParams?.preSandNumShots) currentStageStepdownParams.preSandNumShots = currentStage?.stepdownParams?.numShots;
      currentStageStepdownParams.preSandDperf = (currentStageStepdownParams.preSandDperf * 25.4)?.toFixed(4);
      currentStageStepdownParams.Dperf = (currentStageStepdownParams.Dperf * 25.4)?.toFixed(4);
      currentStageStepdownParams.N = Number((currentStageStepdownParams.N)?.toFixed(1));
      currentStageStepdownParams.preSandNumShots = Number((currentStageStepdownParams.preSandNumShots)?.toFixed(1));
      return currentStageStepdownParams;
    } catch (error) {
      return {};
    };
  });
  
  const [lockedParams, setLockedParams] = useState(currentStage?.stepdownParams?.lockedParams ? Object.keys(currentStage?.stepdownParams?.lockedParams) : []);
  const [recalculate, setRecalculate] = useState(false);

  useEffect(() => {
    setStepdownData(currentStage?.stepdownData);
    const wellName = currentStage?.wellName?.toLowerCase();
    const currentStageStepdownParams = {...currentStage?.stepdownParams} || {};
    if (!currentStageStepdownParams?.fcCasing1) currentStageStepdownParams.fcCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.fc;
    if (!currentStageStepdownParams?.plExpCasing1) currentStageStepdownParams.plExpCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.plExp;
    if (!currentStageStepdownParams?.fcCasing2) currentStageStepdownParams.fcCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.fc;
    if (!currentStageStepdownParams?.plExpCasing2) currentStageStepdownParams.plExpCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.plExp;
    if (!currentStageStepdownParams?.preSandFCCasing1) currentStageStepdownParams.preSandFCCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.fc;
    if (!currentStageStepdownParams?.preSandPLExpCasing1) currentStageStepdownParams.preSandPLExpCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.plExp;
    if (!currentStageStepdownParams?.preSandFCCasing2) currentStageStepdownParams.preSandFCCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.fc;
    if (!currentStageStepdownParams?.preSandPLExpCasing2) currentStageStepdownParams.preSandPLExpCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.plExp;
    if (!currentStageStepdownParams?.preSandNumShots) currentStageStepdownParams.preSandNumShots = currentStage?.stepdownParams?.numShots;
    currentStageStepdownParams.preSandDperf = (+currentStageStepdownParams.preSandDperf * 25.4)?.toFixed(4);
    currentStageStepdownParams.Dperf = (+currentStageStepdownParams.Dperf * 25.4)?.toFixed(4);
    currentStageStepdownParams.N = Number((currentStageStepdownParams.N)?.toFixed(1));
    currentStageStepdownParams.preSandNumShots = Number((currentStageStepdownParams.preSandNumShots)?.toFixed(1));
    setStepdownParams(currentStageStepdownParams);
  }, [currentStage?.stepdownData, currentStage?.stepdownParams, currentRTFO?.settings, currentStage?.wellName]);


  useEffect(() => {
    setPreSandStepdownData(currentStage?.preSandStepdownData);
  }, [currentStage?.preSandStepdownData]);

  return (
    <Dialog 
      open={true} 
      onClose={() => setEditStepdownVisible(false)}
      fullWidth={true}
      maxWidth="xl"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          outline: editMode && '5px solid #f44336'
          // cursor: editMode && 'grab'
        },
        sx: {boxShadow: 1},
        elevation: 0
      }}
    >
      <DialogTitle>Edit {sdtType === 'flush' ? 'Flush' : 'Pre Sand'} Stepdown</DialogTitle>
      <DialogContent>
        
        <div style={{position: 'relative', marginTop: 10, paddingTop: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>

          {editMode && 
            <div style={{zIndex: 69, position: 'absolute', left: '50%', top: -10, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="h6" color="#f44336" >EDIT MODE</Typography>
              <Typography variant="body2" color="#f44336">Click to add or remove points</Typography>
            </div>
          }

          <div style={{display: 'flex', position: 'absolute', top: 10, left: 10, zIndex: 69}}>
            <ToggleButtonGroup
              value={editMode}
              size="small"
              style={{height: 24}}
              onChange={(e, val) => setEditMode(val)}
              exclusive
            >
              <ToggleButton value={true} size="small" title="Edit Points" color={(darkMode ? "" : "primary")}>
                <Edit style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={''}
              size="small"
              style={{height: 24}}
              onChange={() => {
                handleRecalculate(null);
                setLockedParams([]);
              }}
              exclusive
            >
              <ToggleButton value={'restore'} size="small" title="Reset SDT" color={(darkMode ? "" : "primary")}>
                <Restore style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <div style={{display: 'flex', width: '100%', justifyContent: 'space-between'}}>
            <div style={{width: '50%', height: 540}}>
              <RTFOStepdownChart
                stepdownData={stepdownData}
                gridView={true}
                currentRTFO={currentRTFO} 
                darkMode={darkMode}
                showSpikeLines={false} 
                showLegend={true}
                editMode={editMode}
                setStepdownData={setStepdownData}
                setRecalculate={setRecalculate}
                sdtType={sdtType}
                preSandStepdownData={preSandStepdownData}
                setPreSandStepdownData={setPreSandStepdownData}
              />
            </div>

            <div style={{width: '50%', height: 540}}>
              {recalculate && !currentRTFO?.isLoadingData ? 
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 540}}>
                <Button
                  color={darkMode ? "inherit" : "primary"}
                  size="small"
                  style={{color: darkMode ? '#000' : '#fff'}}
                  variant="contained"  
                  startIcon={<Refresh />}
                  // onClick={() => handleRecalculate(stepdownData !== currentStage?.stepdownData ? null : stepdownParams)}
                  onClick={() => handleRecalculate(stepdownParams)}
                >
                  Recalculate
                </Button>
              </div> 
              :
              currentRTFO?.isLoadingData ?
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 540}}>
                <CircularProgress style={{width: 20, height: 20, marginRight: 10}} color="inherit"></CircularProgress>
              </div> 
              :
              <RTFOFlushStepdownChart 
                gridView={true}
                currentRTFO={currentRTFO} 
                darkMode={darkMode}
                sdtType={sdtType}
              />
              }
            </div>


          </div>

          <Divider />

          <Typography variant="h6" style={{marginLeft: 20, marginTop: 20}}>{sdtType === 'flush' ? 'Flush' : 'Pre Sand'} SDT Parameters</Typography>
          <div style={{height: 'auto', marginBottom: 40}}> 
            <RTFOStepdownParams 
              stepdownParams={stepdownParams} 
              setStepdownParams={setStepdownParams} 
              setRecalculate={setRecalculate}
              lockedParams={lockedParams}
              setLockedParams={setLockedParams}
              settings={currentRTFO?.settings}
              sdtType={sdtType}
            />
          </div>

          <Divider />

          {sdtType === 'flush' && 
          <>
            <Typography variant="h6" style={{marginLeft: 20, marginTop: 20}}>ND{<sup>2</sup>} Workflow</Typography>
            <div style={{height: 540, marginBottom: 40, display: 'flex', flexDirection: 'row'}}> 
              <div style={{width: '50%', height: 540}}>
                <RTFOND2StepdownChartMemo 
                  gridView={true}
                  sdtType={sdtType}
                />
              </div>
              <div style={{width: '50%', height: 540}}>
                <RTFOND2StepdownDataTable currentRTFO={currentRTFO} darkMode={darkMode} />
              </div>
            </div>

            <Divider />
          </>
          }

          <Typography variant="h6" style={{marginLeft: 20, marginTop: 20}}>SDT Results</Typography>
          <div style={{height: 400}}> 
            {recalculate && !currentRTFO?.isLoadingData ?
            <div style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200}}>
              <Typography variant="body2" style={{marginLeft: 20, marginTop: 10}}>Recalculate to view stepdown summary</Typography>
            </div> :
            currentRTFO?.isLoadingData ?
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 540}}>
              <CircularProgress style={{width: 20, height: 20, marginRight: 10}} color="inherit"></CircularProgress>
            </div> :
            <RTFOStepdownSummaryTable stepdownParams={stepdownParams} metrics={currentStage?.metrics} perfClosure={currentStage?.events?.perfClosure || []} />}
          </div>

          
        </div>
        
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditStepdownVisible(false)} color={darkMode ? "inherit" : "primary"} style={{marginRight: 10}}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color={darkMode ? "inherit" : "primary"} 
          style={{color: darkMode ? '#000' : '#fff'}} 
          variant="contained" 
          disabled={editMode?.length > 0 || recalculate || currentRTFO?.isLoadingData}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS
  function handleRecalculate(params) {
    try {
    
      setEditMode(null);

      dispatch(setIsLoadingData(true));

      let sdtParams = null;

      if (params) {
        sdtParams = {};
        Object.keys(params)?.forEach(p => {
          if (lockedParams?.indexOf(p) !== -1) {
            if (p === 'preSandDperf' || p === 'Dperf') {
              sdtParams[p] = parseFloat(params[p]) / 25.4;
            } else {
              sdtParams[p] = parseFloat(params[p]);
            };
          };
        });
      };

      socket?.send(JSON.stringify({
        messageType: 'recalculateStepdown',
        padName: currentRTFO?.name,
        stage: {...currentStage, stepdownData, preSandStepdownData},
        perfSchedule: currentRTFO?.perfSchedule,
        surveys: currentRTFO?.surveys,
        params: sdtParams,
        sdtType,
        preSandISIP: +preSandISIP
      }));

      setRecalculate(false);

      if (!params) {
        const wellName = currentStage?.wellName?.toLowerCase();
        const currentStageStepdownParams = {...currentStage?.stepdownParams} || {};
        if (!currentStageStepdownParams?.fcCasing1) currentStageStepdownParams.fcCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.fc;
        if (!currentStageStepdownParams?.plExpCasing1) currentStageStepdownParams.plExpCasing1 = currentRTFO?.settings?.wells?.[wellName]?.casing1?.plExp;
        if (!currentStageStepdownParams?.fcCasing2) currentStageStepdownParams.fcCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.fc;
        if (!currentStageStepdownParams?.plExpCasing2) currentStageStepdownParams.plExpCasing2 = currentRTFO?.settings?.wells?.[wellName]?.casing2?.plExp;
        setStepdownParams(currentStageStepdownParams);
      };

    } catch (error) {
      console.log(error);
    };
  };

  function handleSave() {
    try {

      setEditMode(null);

      socket?.send(JSON.stringify({
        messageType: 'saveStage',
        stage: currentStage
      }));

      setEditStepdownVisible(false);

    } catch (error) {
      console.log(error);
    };
  };


};